import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/session-data';

import type {
	RestrictedUserPrincipal,
	StagedPrincipalsAddMode,
	UserPrincipal,
} from '../commonTypes';
import type { MasterState, MasterStateSetter } from '../../controller/masterState';
// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/Controller';
import type { ControllerExtras } from '../../controller/Controller';

import type {
	BlockedAccessRestrictionsQuery as BlockedAccessRestrictionsQueryType,
	BlockedAccessRestrictionsQueryVariables,
	BlockedAccessSubjectInput,
} from './queries/__types__/BlockedAccessRestrictionsQuery';
import {
	BlockedAccessSubjectType,
	ResourceAccessType,
} from './queries/__types__/BlockedAccessRestrictionsQuery';
import { BlockedAccessRestrictionsQuery } from './queries/BlockedAccessRestrictionsQuery.graphql';
import { UsersContentAccessQuery } from './queries/UsersContentAccessQuery.graphql';
import type {
	UsersContentAccessQueryVariables,
	UsersContentAccessQuery as UsersContentAccessQueryType,
} from './queries/__types__/UsersContentAccessQuery';

const blockedAccessUsersLimit = 10;
const blockedAccessLevelsLimit = 10;

export type ValidateRestrictedPrincipalsProps = {
	contentId: string;
	cloudId: string;
	edition: ConfluenceEdition;
	numberOfAncestors: number;
	addMode: StagedPrincipalsAddMode;
	stagedUserPrincipals: UserPrincipal[];
	setMasterState: MasterStateSetter;
	fireAnalyticEvent: ControllerExtras['fireAnalyticEvent'];
};

export const validateRestrictedPrincipals = async ({
	contentId,
	cloudId,
	edition,
	numberOfAncestors,
	addMode,
	stagedUserPrincipals,
	setMasterState,
	fireAnalyticEvent,
}: ValidateRestrictedPrincipalsProps): Promise<{
	showWarnRestrictionsDialog: boolean;
	showResolveRestrictionsDialog: boolean;
}> => {
	const client = getApolloClient();

	//The validation  is skipped if free edition
	if (edition == ConfluenceEdition.FREE) {
		return {
			showWarnRestrictionsDialog: false,
			showResolveRestrictionsDialog: false,
		};
	}

	//The validation  is skipped if staged user count is 0
	if (stagedUserPrincipals.length == 0) {
		return {
			showWarnRestrictionsDialog: false,
			showResolveRestrictionsDialog: false,
		};
	}
	// if staged user count is greater than 10,
	// or content is more than 10 levels deep
	if (
		stagedUserPrincipals.length > blockedAccessUsersLimit ||
		numberOfAncestors > blockedAccessLevelsLimit
	) {
		fireAnalyticEvent({
			eventType: 'sendOperationalEvent',
			action: 'BlockedAccessCheckLimitExceeded',
			actionSubject: 'BlockedAccessCheck',
			source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
			attributes: {
				NewUsersCount: stagedUserPrincipals.length,
				numberOfAncestors,
			},
		});

		// if ga FG is OFF then skip  usersContentAccess query
		if (!fg('confluence_frontend_unified_restrict_and_share_ga')) {
			return {
				showWarnRestrictionsDialog: false,
				showResolveRestrictionsDialog: false,
			};
		}

		const accountIds = stagedUserPrincipals.map((user) => {
			return user.id;
		});

		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data: usersContentAccessData, errors: usersContentAccessErrors } = await client.query<
			UsersContentAccessQueryType,
			UsersContentAccessQueryVariables
		>({
			query: UsersContentAccessQuery,
			variables: {
				accessType: addMode == 'as-editor' ? ResourceAccessType.EDIT : ResourceAccessType.VIEW,
				accountIds,
				cloudId,
				contentId,
			},
			fetchPolicy: 'network-only',
		});

		if (usersContentAccessErrors) {
			fireAnalyticEvent({
				eventType: 'sendOperationalEvent',
				action: 'errored',
				actionSubject: 'UsersContentAccessQuery',
				actionSubjectId: 'validateRestrictedPrincipals',
				attributes: {
					message: usersContentAccessErrors[0]?.message,
				},
			});
			return {
				showWarnRestrictionsDialog: false,
				showResolveRestrictionsDialog: false,
			};
		}
		const canNotAccessList = usersContentAccessData?.confluence_userContentAccess?.cannotAccessList;

		if (canNotAccessList != null && canNotAccessList.length > 0) {
			const restrictedUserPrincipalsList = canNotAccessList.map((userId) => {
				const restrictedPrincipal: RestrictedUserPrincipal = {
					userPrincipal: stagedUserPrincipals.find((val) => val.id === userId),
					restrictedResources: null,
				};
				return restrictedPrincipal;
			});
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					restrictedUserPrincipals: restrictedUserPrincipalsList,
				}),
			);
			return {
				showWarnRestrictionsDialog: true,
				showResolveRestrictionsDialog: false,
			};
		}
	} else {
		const blockedAccessSubjects = stagedUserPrincipals.map((user) => {
			const blockedAccessSubject: BlockedAccessSubjectInput = {
				subjectId: user.id,
				subjectType: BlockedAccessSubjectType.USER,
			};
			return blockedAccessSubject;
		});

		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data, errors } = await client.query<
			BlockedAccessRestrictionsQueryType,
			BlockedAccessRestrictionsQueryVariables
		>({
			query: BlockedAccessRestrictionsQuery,
			variables: {
				contentId,
				accessType: addMode == 'as-editor' ? ResourceAccessType.EDIT : ResourceAccessType.VIEW,
				subjects: blockedAccessSubjects,
			},
			fetchPolicy: 'network-only',
		});

		if (errors) {
			fireAnalyticEvent({
				eventType: 'sendOperationalEvent',
				action: 'errored',
				actionSubject: 'BlockedAccessRestrictionsQuery',
				actionSubjectId: 'validateRestrictedPrincipals',
				attributes: {
					message: errors[0]?.message,
				},
			});
		}

		if (data?.blockedAccessRestrictions?.blockedAccessRestrictionSummary.length > 0) {
			const restrictedUserPrincipalsList =
				data.blockedAccessRestrictions.blockedAccessRestrictionSummary.map((restrictedUser) => {
					const restrictedPrincipal: RestrictedUserPrincipal = {
						userPrincipal: stagedUserPrincipals.find(
							(val) => val.id === restrictedUser?.subject?.subjectId,
						),
						restrictedResources: restrictedUser?.restrictedResources,
					};
					return restrictedPrincipal;
				});
			const assignableSpaceRolesIntersection =
				data.blockedAccessRestrictions.blockedAccessAssignableSpaceRolesIntersection;
			setMasterState(
				(s: MasterState): MasterState => ({
					...s,
					restrictedUserPrincipals: restrictedUserPrincipalsList,
					assignableSpaceRolesIntersection,
				}),
			);
			//current user is not empowered to fix at least one of the selected user's restrictions
			if (!data.blockedAccessRestrictions.canFixRestrictionsForAllSubjects) {
				return {
					showWarnRestrictionsDialog: true,
					showResolveRestrictionsDialog: false,
				};
			}
			return {
				showWarnRestrictionsDialog: false,
				showResolveRestrictionsDialog: true,
			};
		}
	}
	// all added principles can access or blocked access was skipped
	return {
		showWarnRestrictionsDialog: false,
		showResolveRestrictionsDialog: false,
	};
};
